import { useDashboard, useSettlements } from '@/api/queries'
import { BankIcon } from '@/components/icons'
import { td, th } from '@/components/ui/Controls'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { describeError } from '@/lib/errors'
import { count, date, money } from '@/lib/format'
import { cn } from '@/lib/utils'

export function SettlementsPage() {
  const query = useSettlements()
  const totals = useDashboard(30).data?.totals
  const rows = query.data ?? []
  const paid = rows.reduce((sum, s) => sum + s.amountCents, 0)
  const items = rows.reduce((sum, s) => sum + s.commissionsCount, 0)

  return (
    <>
      <PageHeader title="Settlements" description="Payouts JobSlide has made to you. Each one settles every pending commission earned up to its date." />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="Total paid out" value={query.data ? money(paid) : undefined} />
        <Figure label="Settlements" value={query.data ? count(rows.length) : undefined} />
        <Figure label="Commissions settled" value={query.data ? count(items) : undefined} />
        <Figure label="Waiting for the next one" value={totals ? money(totals.pendingCents) : undefined} tone="brand" />
      </div>

      {query.isError && !query.data ? (
        <div className="card">
          <ErrorState {...describeError(query.error, 'Could not load your settlements')} onRetry={() => void query.refetch()} />
        </div>
      ) : query.isLoading ? (
        <div className="card space-y-3 p-5">
          <Skeleton className="h-11" />
          <Skeleton className="h-11" />
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<BankIcon className="h-8 w-8" />}
            title="No payouts yet"
            description="When JobSlide settles your pending commissions, each payout is listed here with its method and reference. Add your payout details under Account so it reaches you."
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <ul className="divide-y divide-line md:hidden">
            {rows.map((s) => (
              <li key={s.id} className="px-4 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[14.5px] font-medium text-ink">{date(s.createdAt)}</p>
                    <p className="text-[12.5px] text-ink-muted">
                      {count(s.commissionsCount)} commissions up to {date(s.periodTo)}
                    </p>
                  </div>
                  <p className="num shrink-0 text-[15px] font-semibold text-ink">{money(s.amountCents)}</p>
                </div>
                {(s.method || s.reference || s.note) && (
                  <p className="mt-1.5 text-[12.5px] text-ink-soft">
                    {[s.method, s.reference && <span key="r" className="num">{s.reference}</span>].filter(Boolean).map((part, i) => (
                      <span key={i}>{i > 0 && ' · '}{part}</span>
                    ))}
                    {s.note && <span className="block text-ink-muted">{s.note}</span>}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full">
              <thead className="border-b border-line bg-surface-muted/60">
                <tr>
                  <th className={th}>Date</th>
                  <th className={cn(th, 'text-right')}>Amount</th>
                  <th className={cn(th, 'text-right')}>Items</th>
                  <th className={th}>Covers up to</th>
                  <th className={th}>Method</th>
                  <th className={th}>Reference</th>
                  <th className={th}>Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((s) => (
                  <tr key={s.id} className="hover:bg-surface-muted/50">
                    <td className={td}>{date(s.createdAt)}</td>
                    <td className={cn(td, 'num text-right font-semibold')}>{money(s.amountCents)}</td>
                    <td className={cn(td, 'num text-right text-ink-soft')}>{count(s.commissionsCount)}</td>
                    <td className={cn(td, 'text-ink-soft')}>{date(s.periodTo)}</td>
                    <td className={cn(td, 'text-ink-soft')}>{s.method || '—'}</td>
                    <td className={cn(td, 'num text-ink-soft')}>{s.reference || '—'}</td>
                    <td className={cn(td, 'max-w-[280px] truncate whitespace-normal text-ink-muted')}>{s.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-line-strong bg-surface-muted/60">
                <tr>
                  <td className={cn(td, 'font-semibold')}>Total</td>
                  <td className={cn(td, 'num text-right font-semibold')}>{money(paid)}</td>
                  <td className={cn(td, 'num text-right font-semibold')}>{count(items)}</td>
                  <td className={td} colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

function Figure({ label, value, tone }: { label: string; value?: string; tone?: 'brand' }) {
  return (
    <div className="card min-w-0 px-4 py-3.5 sm:px-5 sm:py-4">
      <p className="truncate text-[12.5px] font-medium text-ink-muted sm:text-[13px]">{label}</p>
      {value == null ? <Skeleton className="mt-1.5 h-6 w-20" /> : <p className={cn('num mt-1 truncate text-[20px] font-semibold sm:text-[24px]', tone === 'brand' ? 'text-brand' : 'text-ink')}>{value}</p>}
    </div>
  )
}
